import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route,Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import MainLayout from './layouts/main';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import ChangePasswordLayout from './layouts/ChangePasswordLayout';
import themeService from './services/ThemeService';

import SignIn from './pages/Authentication/SignIn';
import SignUp from './pages/Authentication/SignUp';
import Dashboard from './pages/Dashboard/Dashboard';
import ForgotEmail from './pages/Authentication/ForgotEmail';
import ForgotPassword from './pages/Authentication/ForgotPassword';
import VerifyOTP from './pages/Authentication/VerifyOTP';
import EmailVerification from './pages/Authentication/EmailVerification';
import HomeWidgets from './pages/Home/HomeWidgets';
import AboutUs from './pages/AboutUs/AboutUs';
import Products from './pages/Products/Products';
import Support from './pages/Support/Support';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import CookieSettings from './components/common/CookieSettings';
import TermsAndConditions from './pages/TermsAndConditions/Terms&Conditions';
import Profile from './pages/Profile/Profile';
import EventSharing from './pages/Event/EventSharing';
import EventFeedback from './pages/Event/EventFeedback';
import { ToastContainer } from "react-toastify";
import ZoomCallback from './pages/VideoConference/Callbacks/ZoomCallback';
import GoogleCallback from './pages/VideoConference/Callbacks/GoogleCallback';
import ZohoCallback from './pages/Email/Callbacks/ZohoCallback';
import ZoomIntegrationGuide from './pages/ZoomIntegrationGuide/ZoomIntegrationGuide';
import { getAccessToken } from './utils/authStorage';
import UserSessionManager from './utils/UserSessionManager';
import FeatureFlagService from './services/FeatureFlagService';

function getDefaultAuthenticatedPath() {
  if (!getAccessToken()) return '/home';
  if (UserSessionManager.requiresPasswordChange()) return '/changepassword';
  return '/dashboard';
}


function App() {
  // Initialize theme on app start
  useEffect(() => {
    themeService.setTheme('Default');
  }, []);

  // Refresh feature flags on boot for a returning, already-authenticated
  // session (e.g. page refresh) — otherwise flag-gated tabs stay stuck on
  // whatever was cached at last login, potentially stale or empty.
  useEffect(() => {
    if (getAccessToken()) FeatureFlagService.fetchAndCache();
  }, []);

  return (
    <>
      <ToastContainer />
      <Router>
        <Routes>
          <Route
            path="/"
            element={<Navigate to={getDefaultAuthenticatedPath()} replace />}
          />
          <Route
            path="/home"
            element={
              <MainLayout>
                <HomeWidgets />
              </MainLayout>
            }
          />
          <Route
            path="/signin"
            element={
              <MainLayout>
                <SignIn />
              </MainLayout>
            }
          />
          <Route
            path="/signup"
            element={
              <MainLayout>
                <SignUp />
              </MainLayout>
            }
          />

          <Route
            path="/dashboard"
            element={
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            }
          />

          <Route
            path="/forgotemail"
            element={
              <MainLayout>
                <ForgotEmail />
              </MainLayout>
            }
          />

          <Route
            path="/forgotpassword"
            element={
              <MainLayout>
                <ForgotPassword />
              </MainLayout>
            }
          />

          <Route
            path="/verifyotp"
            element={
              <MainLayout>
                <VerifyOTP />
              </MainLayout>
            }
          />

          <Route path="/changepassword" element={<ChangePasswordLayout />} />

          <Route
            path="/emailverification"
            element={
              <MainLayout>
                <EmailVerification />
              </MainLayout>
            }
          />

          <Route
            path="/about"
            element={
              <MainLayout>
                <AboutUs />
              </MainLayout>
            }
          />

          <Route
            path="/products"
            element={
              <MainLayout>
                <Products />
              </MainLayout>
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <MainLayout>
                <PrivacyPolicy />
              </MainLayout>
            }
          />

          <Route
            path="/terms-and-conditions"
            element={
              <MainLayout>
                <TermsAndConditions />
              </MainLayout>
            }
          />

          <Route
            path="/zoom-integration-guide"
            element={
              <MainLayout>
                <ZoomIntegrationGuide />
              </MainLayout>
            }
          />

          <Route
            path="/cookie-settings"
            element={
              <MainLayout>
                <CookieSettings />
              </MainLayout>
            }
          />

          <Route
            path="/support"
            element={
              <MainLayout>
                <Support />
              </MainLayout>
            }
          />

          <Route
            path="/*"
            element={
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            }
          />

          <Route
            path="/myprofile"
            element={
              <AuthenticatedLayout>
                <Profile activeTab="details" />
              </AuthenticatedLayout>
            }
          />

          <Route
            path="/security"
            element={
              <AuthenticatedLayout>
                <Profile activeTab="security" />
              </AuthenticatedLayout>
            }
          />

          <Route
            path="/company-profile"
            element={
              <AuthenticatedLayout>
                <Profile activeTab="company" />
              </AuthenticatedLayout>
            }
          />

          <Route
            path="/view-event/:eventId"
            element={
              <EventSharing />
            }
          />

          <Route
            path="/event-feedback/:eventId?"
            element={<EventFeedback />}
          />

          <Route path="/zoom/callback" element={<ZoomCallback />} />
           <Route path="/google/callback" element={<GoogleCallback />} />
           {/* Same path already registered as the Zoho OAuth app's redirect URI
               (previously served by the static zoho-oauth-callback.html) so this
               route can replace it with no re-registration in Zoho's console. */}
           <Route path="/zoho-oauth-callback.html" element={<ZohoCallback />} />

        </Routes>
      </Router>
    </>
  );
}

export default App;
